import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import {
  SessionService,
  SessionState,
} from 'src/app/shared/services/session.service';

/**
 * Small, toggleable diagnostic bar shown at the top of the screen that
 * surfaces live session information (signed-in user, login time, session
 * expiry, token metadata, time remaining before expiry, status).
 *
 * Hosts two popups:
 *  - a warning popup shown shortly before the session expires, and
 *  - an expired popup that prompts the user to sign back in.
 */
@Component({
    selector: 'app-session-info-bar',
    templateUrl: './session-info-bar.component.html',
    styleUrls: ['./session-info-bar.component.scss'],
    standalone: false
})
export class SessionInfoBarComponent implements OnInit, OnDestroy {
  state: SessionState | null = null;
  expanded = false;

  private sub: Subscription | null = null;

  constructor(
    public appService: AppService,
    public sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.sub = this.sessionService.state$.subscribe((s) => (this.state = s));
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /** Keep the (rolling) backend session alive, then refresh the countdown. */
  stayLoggedIn(): void {
    this.sessionService.extend();
  }

  /** User chose to log out immediately from the warning popup. */
  logoutNow(): void {
    this.sessionService.forceLogout();
  }

  /** User clicked "Sign back in" on the expired popup. */
  signBackIn(): void {
    this.sessionService.forceLogout();
  }

  /** Format seconds as m:ss for display. */
  formatTime(totalSeconds: number): string {
    const s = Math.max(totalSeconds || 0, 0);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
